import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Toaster, toast } from 'sonner';
import Footer from '../components/Footer';
import {
  ArrowLeft,
  Settings as SettingsIcon,
  Key,
  Copy,
  RefreshCw,
  Eye,
  EyeOff,
  Code2,
  Loader2,
  CheckCircle2,
  Webhook,
  Fingerprint,
  Trash2,
  ShieldCheck,
  Users as UsersIcon,
  UserPlus,
  KeyRound,
  Send,
  Bell
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { isPasskeySupported, registerPasskey, listPasskeys, deletePasskey } from '../utils/passkey';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PUBLIC_WEBHOOK = `${process.env.REACT_APP_BACKEND_URL}/api/public/leads`;

const SettingsPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [apiKey, setApiKey] = useState('');
  const [loading, setLoading] = useState(true);
  const [regenerating, setRegenerating] = useState(false);
  const [visible, setVisible] = useState(false);
  const [tab, setTab] = useState('autoinject');
  const [passkeys, setPasskeys] = useState([]);
  const [registeringKey, setRegisteringKey] = useState(false);

  // Admin: gestione utenti
  const [users, setUsers] = useState([]);
  const [showNewUser, setShowNewUser] = useState(false);
  const [newUser, setNewUser] = useState({ email: '', name: '', company: '', password: '', role: 'user' });
  const [creatingUser, setCreatingUser] = useState(false);

  // Telegram settings
  const [tg, setTg] = useState({ bot_token: '', chat_id: '', enabled: false, bot_token_masked: '', has_token: false });
  const [savingTg, setSavingTg] = useState(false);
  const [testingTg, setTestingTg] = useState(false);

  const fetchPasskeys = async () => {
    try {
      const list = await listPasskeys(axios);
      setPasskeys(list);
    } catch { /* silent */ }
  };

  const addPasskey = async () => {
    if (!isPasskeySupported()) {
      toast.error('Passkey non supportato da questo browser');
      return;
    }
    setRegisteringKey(true);
    try {
      const name = window.prompt('Dai un nome a questo dispositivo (es. "MacBook Marco")', 'Passkey');
      if (name === null) { setRegisteringKey(false); return; }
      await registerPasskey(axios, name || 'Passkey');
      toast.success('Passkey registrato');
      await fetchPasskeys();
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || 'Registrazione passkey fallita';
      toast.error(typeof msg === 'string' ? msg : 'Registrazione passkey fallita');
    } finally {
      setRegisteringKey(false);
    }
  };

  const removePasskey = async (id) => {
    if (!window.confirm('Rimuovere questo passkey?')) return;
    try {
      await deletePasskey(axios, id);
      toast.success('Passkey rimosso');
      await fetchPasskeys();
    } catch {
      toast.error('Errore rimozione');
    }
  };

  // Admin: fetch users
  const fetchUsers = async () => {
    try {
      const res = await axios.get(`${API_URL}/admin/users`);
      setUsers(res.data.users || []);
    } catch { /* silent per non-admin */ }
  };

  const createUser = async () => {
    if (!newUser.email || !newUser.password || !newUser.name) {
      toast.error('Compila email, nome e password');
      return;
    }
    if (newUser.password.length < 6) {
      toast.error('Password minima 6 caratteri');
      return;
    }
    setCreatingUser(true);
    try {
      await axios.post(`${API_URL}/admin/users`, newUser);
      toast.success(`Utente ${newUser.email} creato`);
      setNewUser({ email: '', name: '', company: '', password: '', role: 'user' });
      setShowNewUser(false);
      await fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore creazione utente');
    } finally {
      setCreatingUser(false);
    }
  };

  const deleteUser = async (uid, email) => {
    if (!window.confirm(`Eliminare l'utente ${email}? L'operazione è irreversibile.`)) return;
    try {
      await axios.delete(`${API_URL}/admin/users/${uid}`);
      toast.success('Utente eliminato');
      await fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore');
    }
  };

  const resetUserPassword = async (uid, email) => {
    const newPass = window.prompt(`Nuova password per ${email} (minimo 6 caratteri):`);
    if (!newPass) return;
    if (newPass.length < 6) { toast.error('Password troppo corta'); return; }
    try {
      await axios.post(`${API_URL}/admin/users/${uid}/reset-password`, { password: newPass });
      toast.success('Password aggiornata');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore');
    }
  };

  // Telegram
  const fetchTelegram = async () => {
    try {
      const res = await axios.get(`${API_URL}/settings/telegram`);
      setTg({ ...tg, ...res.data, bot_token: '' });
    } catch { /* silent */ }
  };

  const saveTelegram = async () => {
    setSavingTg(true);
    try {
      const payload = {
        chat_id: tg.chat_id,
        enabled: tg.enabled,
      };
      // Invia il bot_token solo se l'utente ha inserito una nuova stringa
      if (tg.bot_token && tg.bot_token.trim()) payload.bot_token = tg.bot_token.trim();
      await axios.post(`${API_URL}/settings/telegram`, payload);
      toast.success('Impostazioni Telegram salvate');
      await fetchTelegram();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore');
    } finally {
      setSavingTg(false);
    }
  };

  const testTelegram = async () => {
    setTestingTg(true);
    try {
      await axios.post(`${API_URL}/settings/telegram/test`);
      toast.success('Notifica di test inviata! Controlla Telegram.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Test fallito');
    } finally {
      setTestingTg(false);
    }
  };

  const fetchKey = async () => {
    try {
      const res = await axios.get(`${API_URL}/settings/api-key`);
      setApiKey(res.data.api_key);
    } catch {
      toast.error('Errore nel recupero della API key');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchKey();
    fetchPasskeys();
    fetchTelegram();
    if (isAdmin) fetchUsers();
  }, [isAdmin]);

  const regenerate = async () => {
    if (!window.confirm('Rigenerando la chiave, il vecchio webhook smetterà di funzionare. Continuare?')) return;
    setRegenerating(true);
    try {
      const res = await axios.post(`${API_URL}/settings/api-key/regenerate`);
      setApiKey(res.data.api_key);
      toast.success('Nuova API key generata');
    } catch {
      toast.error('Errore rigenerazione');
    } finally {
      setRegenerating(false);
    }
  };

  const copy = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copiato!`);
  };

  const webhookUrl = `${PUBLIC_WEBHOOK}?api_key=${apiKey}`;
  const maskedKey = apiKey ? `${apiKey.slice(0, 8)}${'•'.repeat(24)}${apiKey.slice(-4)}` : '';

  const curlExample = `curl -X POST "${webhookUrl}" \\
  -H "Content-Type: application/json" \\
  -d '{
    "first_name": "Marco",
    "last_name": "Rossi",
    "company": "Acme SRL",
    "email": "marco@acme.it",
    "phone": "+39 333 1234567",
    "message": "Vorrei un preventivo"
  }'`;

  const htmlExample = `<form id="cs-form" onsubmit="return submitToCustomerScout(event)">
  <input name="first_name" placeholder="Nome" required />
  <input name="last_name" placeholder="Cognome" required />
  <input name="company" placeholder="Azienda" />
  <input name="email" type="email" placeholder="Email" required />
  <input name="phone" placeholder="Telefono" />
  <button type="submit">Invia</button>
</form>

<script>
async function submitToCustomerScout(e) {
  e.preventDefault();
  const data = Object.fromEntries(new FormData(e.target));
  const res = await fetch("${webhookUrl}", {
    method: "POST",
    headers: {"Content-Type": "application/json"},
    body: JSON.stringify(data)
  });
  if (res.ok) {
    alert("Grazie! Ti contatteremo a breve.");
    e.target.reset();
  } else {
    alert("Errore, riprova.");
  }
  return false;
}
</script>`;

  const cf7Example = `[text* first-name placeholder "Nome"]
[text* last-name placeholder "Cognome"]
[text company placeholder "Azienda"]
[email* your-email placeholder "Email"]
[tel your-tel placeholder "Telefono"]
[submit "Invia"]

<!-- Poi in "Impostazioni Aggiuntive" del form (o via plugin "CF7 to Webhook"): -->
<!-- URL webhook: ${webhookUrl} -->
<!-- Mappatura campi:
     first-name -> first_name
     last-name  -> last_name
     company    -> company
     your-email -> email
     your-tel   -> phone
-->`;

  const elementorExample = `1. Nel widget Form di Elementor, apri "Actions After Submit"
2. Aggiungi l'azione "Webhook"
3. Imposta l'URL:
   ${webhookUrl}
4. Nomina i campi del form ESATTAMENTE così (ID campo):
   - first_name
   - last_name
   - company
   - email
   - phone
5. Salva. I lead arriveranno automaticamente nella colonna "Lead da Form".`;

  const embedScriptUrl = `${process.env.REACT_APP_BACKEND_URL}/api/public/embed.js?api_key=${apiKey}`;
  const autoInjectExample = `<!-- Incolla questo tag UNA SOLA VOLTA nel <head> o prima di </body> del tuo sito -->
<script src="${embedScriptUrl}" defer></script>

<!-- Fatto! Tutti i <form> della pagina invieranno automaticamente i lead a Customer Scout.
     Lo script riconosce i campi standard: name/nome, email, phone/telefono, company/azienda, ecc.

Opzioni avanzate (attributi facoltativi sul form o sui campi):
  <form data-cs-source="landing-servizi">        <!-- etichetta origine visibile in CRM -->
  <form data-cs-ignore>                          <!-- ignora questo form -->
  <input data-cs-field="phone" name="tel_azienda"> <!-- forza il mapping di un campo -->
-->`;

  const activeSnippet = { curl: curlExample, html: htmlExample, cf7: cf7Example, elementor: elementorExample, autoinject: autoInjectExample }[tab];

  return (
    <div className="min-h-screen bg-slate-50">
      <Toaster position="top-right" richColors />
      <header className="glass-header sticky top-0 z-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Button variant="ghost" size="sm" onClick={() => navigate('/dashboard')} data-testid="back-to-dashboard">
              <ArrowLeft className="w-4 h-4 mr-2" /> Dashboard
            </Button>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-slate-800 text-white flex items-center justify-center">
                <SettingsIcon className="w-5 h-5" />
              </div>
              <span className="font-bold text-xl text-slate-900 tracking-tight">Impostazioni</span>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">

        {/* API KEY Card */}
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Key className="w-5 h-5" /> API Key Webhook
            </CardTitle>
            <CardDescription>
              Chiave segreta per autenticare i lead inviati al tuo webhook. Trattala come una password.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {loading ? (
              <div className="flex justify-center py-6"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
            ) : (
              <>
                <div className="flex items-center gap-2">
                  <code className="flex-1 font-mono text-sm bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 truncate" data-testid="api-key-display">
                    {visible ? apiKey : maskedKey}
                  </code>
                  <Button variant="outline" size="icon" onClick={() => setVisible(!visible)} data-testid="toggle-key-visibility">
                    {visible ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </Button>
                  <Button variant="outline" size="icon" onClick={() => copy(apiKey, 'API key')} data-testid="copy-api-key">
                    <Copy className="w-4 h-4" />
                  </Button>
                  <Button variant="outline" onClick={regenerate} disabled={regenerating} data-testid="regenerate-btn">
                    {regenerating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                    Rigenera
                  </Button>
                </div>
                <div className="pt-4 border-t border-slate-100">
                  <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">URL Webhook</label>
                  <div className="flex items-center gap-2 mt-1">
                    <code className="flex-1 font-mono text-xs bg-slate-900 text-emerald-300 rounded-lg px-3 py-2 truncate" data-testid="webhook-url">
                      {webhookUrl}
                    </code>
                    <Button variant="outline" size="icon" onClick={() => copy(webhookUrl, 'URL webhook')} data-testid="copy-webhook-url">
                      <Copy className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Snippets */}
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Code2 className="w-5 h-5" /> Snippet di Integrazione
            </CardTitle>
            <CardDescription>
              Copia e incolla lo snippet corretto per il tuo tipo di form
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex flex-wrap gap-2" data-testid="snippet-tabs">
              {[
                { id: 'autoinject', label: '⚡ Auto-Inject (raccomandato)' },
                { id: 'curl', label: 'cURL (test)' },
                { id: 'html', label: 'HTML/JS custom' },
                { id: 'cf7', label: 'Contact Form 7' },
                { id: 'elementor', label: 'Elementor' },
              ].map(t => (
                <button
                  key={t.id}
                  onClick={() => setTab(t.id)}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium border-2 transition ${
                    tab === t.id
                      ? 'border-slate-800 bg-slate-800 text-white'
                      : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'
                  }`}
                  data-testid={`tab-${t.id}`}
                >
                  {t.label}
                </button>
              ))}
            </div>

            <div className="relative">
              <pre className="bg-slate-900 text-slate-100 rounded-lg p-4 overflow-x-auto text-xs font-mono whitespace-pre-wrap" data-testid="snippet-code">
                {activeSnippet}
              </pre>
              <Button
                variant="outline"
                size="sm"
                onClick={() => copy(activeSnippet, 'Snippet')}
                className="absolute top-2 right-2 bg-white/90 hover:bg-white"
                data-testid="copy-snippet"
              >
                <Copy className="w-3 h-3 mr-1" /> Copia
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Fields reference */}
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Webhook className="w-5 h-5" /> Campi Accettati
            </CardTitle>
            <CardDescription>
              I campi che il webhook riconosce. Tutti opzionali, ma almeno uno tra nome/azienda/email deve essere presente.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200">
                    <th className="text-left py-2 px-3 font-semibold text-slate-600 text-xs">Campo</th>
                    <th className="text-left py-2 px-3 font-semibold text-slate-600 text-xs">Descrizione</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    ['first_name', 'Nome'],
                    ['last_name', 'Cognome'],
                    ['name', 'Alternativa a first_name + last_name (nome completo)'],
                    ['company', 'Azienda'],
                    ['vat', 'P.IVA (verrà verificata automaticamente su VIES)'],
                    ['email', 'Email'],
                    ['phone', 'Telefono'],
                    ['message', 'Messaggio libero dal form'],
                    ['source', 'Etichetta origine (es. "landing-servizi") — opzionale'],
                  ].map(([k, d]) => (
                    <tr key={k} className="border-b border-slate-100">
                      <td className="py-2 px-3 font-mono text-xs text-purple-700">{k}</td>
                      <td className="py-2 px-3 text-slate-600">{d}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="mt-4 p-3 bg-emerald-50 border border-emerald-200 rounded-lg flex items-start gap-2">
              <CheckCircle2 className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" />
              <div className="text-xs text-emerald-800">
                I lead ricevuti finiscono automaticamente nella pipeline CRM sotto la colonna <b>"Lead da Form"</b>,
                marcati come non letti. L'email viene salvata anche nella <b>Rubrica Email globale</b>.
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Telegram Notifications */}
        <Card className="border-slate-200" data-testid="telegram-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Bell className="w-5 h-5" /> Notifiche Telegram
            </CardTitle>
            <CardDescription>
              Ricevi una notifica <b>push istantanea</b> su Telegram ogni volta che arriva un nuovo lead dal form del tuo sito.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <details className="text-xs bg-blue-50 border border-blue-200 rounded-lg p-3">
              <summary className="cursor-pointer font-medium text-blue-900">Come ottenere Bot Token e Chat ID (3 minuti)</summary>
              <ol className="mt-2 ml-4 list-decimal space-y-1 text-blue-900">
                <li>Apri Telegram e cerca <b>@BotFather</b></li>
                <li>Invia <code className="bg-white px-1 rounded">/newbot</code>, segui le istruzioni → ricevi un <b>Bot Token</b> (formato <code className="bg-white px-1 rounded">123456:AAxxx...</code>)</li>
                <li>Cerca il tuo nuovo bot su Telegram e invia <code className="bg-white px-1 rounded">/start</code> (senza questo passaggio non riceverai messaggi!)</li>
                <li>Per ottenere il <b>Chat ID</b>: cerca <b>@userinfobot</b> su Telegram, avvialo con <code className="bg-white px-1 rounded">/start</code> → ti mostra il tuo ID (es. <code className="bg-white px-1 rounded">123456789</code>)</li>
                <li>Incolla i due valori qui sotto, salva e premi "Invia notifica di test"</li>
              </ol>
            </details>

            <div className="space-y-3">
              <div>
                <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Bot Token</label>
                <input
                  type="password"
                  placeholder={tg.has_token ? `${tg.bot_token_masked} (già configurato — inserisci per sostituire)` : 'es. 123456789:AABBccDDeeFF...'}
                  value={tg.bot_token}
                  onChange={e => setTg({...tg, bot_token: e.target.value})}
                  className="mt-1 w-full h-10 px-3 rounded-md border border-slate-200 bg-white text-sm font-mono focus:outline-none focus:ring-2 focus:ring-slate-300"
                  data-testid="telegram-bot-token"
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Chat ID</label>
                <input
                  type="text"
                  placeholder="es. 123456789"
                  value={tg.chat_id}
                  onChange={e => setTg({...tg, chat_id: e.target.value})}
                  className="mt-1 w-full h-10 px-3 rounded-md border border-slate-200 bg-white text-sm font-mono focus:outline-none focus:ring-2 focus:ring-slate-300"
                  data-testid="telegram-chat-id"
                />
              </div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={tg.enabled}
                  onChange={e => setTg({...tg, enabled: e.target.checked})}
                  className="w-4 h-4"
                  data-testid="telegram-enabled"
                />
                <span className="text-sm text-slate-700">Attiva notifiche Telegram per i nuovi lead</span>
              </label>
              <div className="flex flex-wrap gap-2">
                <Button onClick={saveTelegram} disabled={savingTg} data-testid="save-telegram-btn">
                  {savingTg ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
                  Salva
                </Button>
                <Button
                  variant="outline"
                  onClick={testTelegram}
                  disabled={testingTg || !tg.enabled}
                  data-testid="test-telegram-btn"
                >
                  {testingTg ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                  Invia notifica di test
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Passkey / Sicurezza */}
        <Card className="border-slate-200" data-testid="passkey-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Fingerprint className="w-5 h-5" /> Passkey & Sicurezza
            </CardTitle>
            <CardDescription>
              Accedi al posto della password usando Touch ID, Face ID, Windows Hello o una chiavetta di sicurezza. Più sicuro e più veloce.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="p-3 bg-slate-50 border border-slate-200 rounded-lg flex items-start gap-2">
              <ShieldCheck className="w-4 h-4 text-slate-600 flex-shrink-0 mt-0.5" />
              <div className="text-xs text-slate-700">
                <b>Anti-intrusione attivo</b>: dopo 5 tentativi di login falliti l'account viene bloccato per 15 minuti dallo stesso IP. Le password sono cifrate con bcrypt.
              </div>
            </div>

            {!isPasskeySupported() && (
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-800">
                Il tuo browser non supporta Passkey. Usa Chrome/Safari/Edge aggiornati su un dispositivo con biometria o chiavetta FIDO2.
              </div>
            )}

            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-900">I tuoi Passkey</p>
                <p className="text-xs text-slate-500">{passkeys.length} passkey registrato{passkeys.length !== 1 ? 'i' : ''}</p>
              </div>
              <Button
                onClick={addPasskey}
                disabled={registeringKey || !isPasskeySupported()}
                data-testid="register-passkey-btn"
              >
                {registeringKey ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Fingerprint className="w-4 h-4 mr-2" />}
                Registra Passkey
              </Button>
            </div>

            {passkeys.length > 0 && (
              <div className="border border-slate-200 rounded-lg divide-y divide-slate-100">
                {passkeys.map(pk => (
                  <div key={pk.id} className="flex items-center justify-between p-3">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg bg-emerald-100 text-emerald-700 flex items-center justify-center">
                        <Fingerprint className="w-4 h-4" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-slate-900">{pk.device_name || 'Passkey'}</p>
                        <p className="text-xs text-slate-500">
                          Aggiunto il {new Date(pk.created_at).toLocaleDateString('it-IT', { day: '2-digit', month: 'short', year: 'numeric' })}
                        </p>
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => removePasskey(pk.id)}
                      className="text-red-500 hover:text-red-600 hover:bg-red-50"
                      data-testid={`delete-passkey-${pk.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Admin: Gestione Utenti */}
        {isAdmin && (
          <Card className="border-slate-200" data-testid="admin-users-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <UsersIcon className="w-5 h-5" /> Gestione Utenti <span className="text-xs bg-amber-100 text-amber-800 px-2 py-0.5 rounded-full font-medium">Admin</span>
              </CardTitle>
              <CardDescription>
                Crea, elimina e gestisci gli account. Solo tu (admin) puoi registrare nuovi utenti.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-900">{users.length} utent{users.length === 1 ? 'e' : 'i'} registrat{users.length === 1 ? 'o' : 'i'}</p>
                  <p className="text-xs text-slate-500">Include admin e utenti standard</p>
                </div>
                <Button onClick={() => setShowNewUser(!showNewUser)} data-testid="toggle-new-user-form">
                  <UserPlus className="w-4 h-4 mr-2" /> Nuovo Utente
                </Button>
              </div>

              {showNewUser && (
                <div className="border border-slate-200 rounded-lg p-4 bg-slate-50 space-y-3" data-testid="new-user-form">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <input
                      type="email"
                      placeholder="Email"
                      value={newUser.email}
                      onChange={e => setNewUser({...newUser, email: e.target.value})}
                      className="h-10 px-3 rounded-md border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-slate-300"
                      data-testid="new-user-email"
                    />
                    <input
                      type="text"
                      placeholder="Nome completo"
                      value={newUser.name}
                      onChange={e => setNewUser({...newUser, name: e.target.value})}
                      className="h-10 px-3 rounded-md border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-slate-300"
                      data-testid="new-user-name"
                    />
                    <input
                      type="text"
                      placeholder="Azienda (opzionale)"
                      value={newUser.company}
                      onChange={e => setNewUser({...newUser, company: e.target.value})}
                      className="h-10 px-3 rounded-md border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-slate-300"
                      data-testid="new-user-company"
                    />
                    <input
                      type="password"
                      placeholder="Password iniziale (min 6)"
                      value={newUser.password}
                      onChange={e => setNewUser({...newUser, password: e.target.value})}
                      className="h-10 px-3 rounded-md border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-slate-300"
                      data-testid="new-user-password"
                    />
                    <select
                      value={newUser.role}
                      onChange={e => setNewUser({...newUser, role: e.target.value})}
                      className="h-10 px-3 rounded-md border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-slate-300"
                      data-testid="new-user-role"
                    >
                      <option value="user">Utente standard</option>
                      <option value="admin">Amministratore</option>
                    </select>
                  </div>
                  <div className="flex justify-end gap-2">
                    <Button variant="outline" size="sm" onClick={() => setShowNewUser(false)}>Annulla</Button>
                    <Button onClick={createUser} disabled={creatingUser} data-testid="create-user-btn">
                      {creatingUser ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <UserPlus className="w-4 h-4 mr-2" />}
                      Crea Utente
                    </Button>
                  </div>
                </div>
              )}

              <div className="border border-slate-200 rounded-lg divide-y divide-slate-100 max-h-96 overflow-y-auto">
                {users.map(u => (
                  <div key={u.id} className="flex items-center justify-between p-3 gap-2" data-testid={`user-row-${u.id}`}>
                    <div className="flex items-center gap-3 min-w-0 flex-1">
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${u.role === 'admin' ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-700'}`}>
                        {u.role === 'admin' ? <ShieldCheck className="w-4 h-4" /> : <UsersIcon className="w-4 h-4" />}
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-medium text-slate-900 truncate">{u.name || u.email}</p>
                          {u.role === 'admin' && <span className="text-[10px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded font-semibold">ADMIN</span>}
                          {u.id === user?.id && <span className="text-[10px] bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded font-semibold">TU</span>}
                        </div>
                        <p className="text-xs text-slate-500 truncate">{u.email} {u.company ? `• ${u.company}` : ''}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => resetUserPassword(u.id, u.email)}
                        className="text-slate-500 hover:text-slate-800"
                        title="Cambia password"
                        data-testid={`reset-pass-${u.id}`}
                      >
                        <KeyRound className="w-4 h-4" />
                      </Button>
                      {u.id !== user?.id && (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => deleteUser(u.id, u.email)}
                          className="text-red-500 hover:text-red-600 hover:bg-red-50"
                          title="Elimina utente"
                          data-testid={`delete-user-${u.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
                {users.length === 0 && (
                  <p className="p-4 text-sm text-slate-500 text-center">Nessun utente trovato</p>
                )}
              </div>
            </CardContent>
          </Card>
        )}

      </main>
      <Footer />
    </div>
  );
};

export default SettingsPage;
